import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ProjectForm } from '../components/ProjectForm';
import { projectPath } from '../paths';
import { useCreateProject, useProject, useUpdateProject } from '../queries';
import type { ProjectInput } from '../types';

export function ProjectFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const projectQuery = useProject(isEdit ? publicId : -1);
  const create = useCreateProject();
  const update = useUpdateProject(publicId);

  async function handleSubmit(values: ProjectInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(projectPath(saved));
  }

  if (isEdit && projectQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const project = projectQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/projects/${publicId}` : '/projects')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${project?.name ?? 'project'}` : 'New project'}
        </h1>
      </div>

      <ProjectForm
        submitLabel={isEdit ? 'Save changes' : 'Create project'}
        defaultValues={
          project
            ? {
                name: project.name,
                company: project.company ? String(project.company) : '',
                status: project.status,
                currency: project.currency,
                budget_hours: String(Number(project.budget_hours)),
                budget_amount: String(Number(project.budget_amount)),
                start_date: project.start_date ?? '',
                end_date: project.end_date ?? '',
                description: project.description,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/projects/${publicId}` : '/projects')}
      />
    </div>
  );
}
